export function debounce(func, wait) {
  let timeout;
  return function executedFunction(...args) {
    const later = () => {
      clearTimeout(timeout);
      func(...args);
    };
    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
  };
}

export function getStoreOpenStatus(hoursString) {
  if (!hoursString) return { isOpen: false, closingTime: null };

  const d = new Date();
  const time = `${d.getHours()}.${d.getMinutes()}`;

  if (hoursString.toLowerCase() === 'gesloten') {
    return { isOpen: false, closingTime: null };
  }

  if (hoursString.includes('&')) {
    const [firstPeriod, secondPeriod] = hoursString.split('&');
    const [firstStart, firstEnd] = firstPeriod.split('-');
    const [secondStart, secondEnd] = secondPeriod.split('-');

    if (time >= firstStart && time <= firstEnd) {
      return { isOpen: true, closingTime: firstEnd };
    }
    if (time >= secondStart && time <= secondEnd) {
      return { isOpen: true, closingTime: secondEnd };
    }
    return { isOpen: false, closingTime: null };
  }

  const [start, end] = hoursString.split('-');
  if (time >= start && time <= end) {
    return { isOpen: true, closingTime: end };
  }
  return { isOpen: false, closingTime: null };
}

export function storageAvailable(type) {
  let storage;
  try {
    storage = window[type];
    const x = '__storage_test__';
    storage.setItem(x, x);
    storage.removeItem(x);
    return true;
  } catch (e) {
    return (
      e instanceof DOMException &&
      (e.code === 22 ||
        e.code === 1014 ||
        e.name === 'QuotaExceededError' ||
        e.name === 'NS_ERROR_DOM_QUOTA_REACHED') &&
      storage &&
      storage.length !== 0
    );
  }
}

/**
 * Converts kilometers to the preferred distance unit (km or miles) and returns formatted string
 * @param {number} km - Distance in kilometers
 * @param {string} unit - Preferred unit ('km' or 'miles')
 * @param {string} kmLabel - Label for kilometers
 * @param {string} milesLabel - Label for miles
 * @returns {string} Formatted distance string with unit
 */
export function getDistanceFromKm(km, unit = 'km', kmLabel = 'km', milesLabel = 'mi') {
  let distanceText = '';
  if (unit === 'km') {
    const parsedKm = Math.round(km);
    distanceText = `${parsedKm} ${kmLabel}`;
  } else {
    const miles = km * 0.621371;
    const parsedMiles = Math.round(miles);
    distanceText = `${parsedMiles} ${milesLabel}`;
  }
  return distanceText;
}

/**
 * Converts degrees to radians
 * @param {number} deg - Degrees to convert
 * @returns {number} Value in radians
 */
export function deg2rad(deg) {
  return deg * (Math.PI / 180);
}

/**
 * Calculates the distance between two lat/long points using the Haversine formula
 * @param {number} lat1 - Latitude of first point
 * @param {number} lon1 - Longitude of first point
 * @param {number} lat2 - Latitude of second point
 * @param {number} lon2 - Longitude of second point
 * @param {string} unit - Preferred unit ('km' or 'miles')
 * @param {string} kmLabel - Label for kilometers
 * @param {string} milesLabel - Label for miles
 * @returns {string} Formatted distance string with unit
 */
export function getDistanceFromLatLon(
  lat1,
  lon1,
  lat2,
  lon2,
  unit = 'km',
  kmLabel = 'km',
  milesLabel = 'mi',
) {
  const R = 6371; // Radius of the earth in km
  const dLat = deg2rad(lat2 - lat1);
  const dLon = deg2rad(lon2 - lon1);
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(deg2rad(lat1)) * Math.cos(deg2rad(lat2)) * Math.sin(dLon / 2) * Math.sin(dLon / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  const d = R * c; // Distance in km
  return getDistanceFromKm(d, unit, kmLabel, milesLabel);
}
